import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  buildRollSections,
  shouldShowRollCheckbox,
  getRollCheckboxLabel,
  buildDownloadRequest,
  getSelectedCount,
} from '../utils/rollUtils';
import type {
  ScansResponse,
  RollGroup,
  DownloadFormat,
  JobStatus,
  DownloadPart,
} from '../types/roll';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './DownloadPage.module.css';

/**
 * Delay between sequential part downloads. A short gap keeps the browser from
 * dropping back-to-back navigations when auto-downloading several ZIPs.
 */
const PART_DOWNLOAD_DELAY_MS = 800;

interface DownloadJobResponse {
  job_id: string;
  status: JobStatus;
  /** Top-level convenience URL for single-part jobs (backward compatible). */
  presigned_url?: string;
  presigned_expires_at?: string;
  /** Number of ZIP parts. Absent or 1 for single-part jobs. */
  part_count?: number;
  /** Ordered (ascending `part_no`) list of parts for multi-part jobs. */
  parts?: DownloadPart[];
  error?: string;
  scan_errors?: Array<{ scan_id: string; error: string }>;
}

async function fetchScans(orderId: string): Promise<ScansResponse> {
  const res = await fetch(`/api/v1/orders/${orderId}/scans`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch scans (${res.status})`);
  }
  return res.json();
}

async function initiateDownload(
  format: DownloadFormat,
  scanIds: string[],
  rollIds: string[]
): Promise<{ job_id: string }> {
  const body: Record<string, unknown> = { format };
  if (rollIds.length > 0) body.roll_ids = rollIds;
  if (scanIds.length > 0) body.scan_ids = scanIds;

  const res = await fetch('/api/v1/downloads', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`Failed to initiate download (${res.status})`);
  }
  return res.json();
}

async function pollDownloadStatus(jobId: string): Promise<DownloadJobResponse> {
  const res = await fetch(`/api/v1/downloads/${jobId}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to poll download status (${res.status})`);
  }
  return res.json();
}

async function refreshDownloadUrl(jobId: string): Promise<DownloadJobResponse> {
  const res = await fetch(`/api/v1/downloads/${jobId}/refresh`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to refresh download URL (${res.status})`);
  }
  return res.json();
}

async function retryDownloadPart(jobId: string, partNo: number): Promise<void> {
  const res = await fetch(`/api/v1/downloads/${jobId}/parts/${partNo}/retry`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to retry part ${partNo} (${res.status})`);
  }
}

function isUrlExpired(expiresAt: string | undefined): boolean {
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() <= Date.now();
}

/**
 * Trigger a browser download for a single part by clicking a temporary
 * anchor. Using an anchor (rather than `window.location`) lets us fire
 * several downloads in sequence without the later ones clobbering earlier
 * navigations.
 */
function triggerPartDownload(url: string, filename?: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  if (filename) anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/** A job is multi-part when it has more than one part. */
function isMultiPart(result: Pick<DownloadJobResponse, 'parts'>): boolean {
  return !!result.parts && result.parts.length > 1;
}

const FORMAT_LABELS: Record<DownloadFormat, { label: string; description: string }> = {
  social_jpeg: { label: 'Social JPEG', description: '≤2048px, ≤2 MB' },
  print_jpeg: { label: 'Print JPEG', description: 'Full resolution' },
  archival_tiff: { label: 'Archival TIFF', description: 'Original master file' },
};

export function DownloadPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [selectedScans, setSelectedScans] = useState<Set<string>>(new Set());
  const [selectedRolls, setSelectedRolls] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadTarget, setDownloadTarget] = useState<'all' | 'selected'>('all');
  const [jobId, setJobId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [lastFormat, setLastFormat] = useState<DownloadFormat | null>(null);
  const [lastScanIds, setLastScanIds] = useState<string[]>([]);
  const [lastRollIds, setLastRollIds] = useState<string[]>([]);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  // Multi-part download state. `jobResult` holds the latest poll response for a
  // multi-part job so the parts panel can render live per-part status.
  const [jobResult, setJobResult] = useState<DownloadJobResponse | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<
    { current: number; total: number } | null
  >(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sequentialRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, error } = useQuery<ScansResponse>({
    queryKey: ['scans', orderId],
    queryFn: () => fetchScans(orderId!),
    enabled: !!orderId,
  });

  usePageTitle(data?.order_number ? `Download — ${data.order_number}` : 'Download');

  const rolls: RollGroup[] = data?.rolls ?? [];

  const rollSections = useMemo(() => buildRollSections(rolls), [rolls]);

  const allScans = useMemo(
    () => rolls.flatMap((r) => r.scans),
    [rolls]
  );

  // Whether this order contains any customer edits. Edits have no TIFF master,
  // so under the archival option they are delivered as their print-quality
  // JPEG — the download modal calls this out when it applies.
  const hasEdits = useMemo(
    () =>
      rolls.some(
        (r) => r.roll_type === 'edits' || r.scans.some((s) => s.source_scan_id)
      ),
    [rolls]
  );

  const allSelected =
    allScans.length > 0 && allScans.every((s) => selectedScans.has(s.scan_id));

  // Cleanup polling and any in-flight sequential download on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (sequentialRef.current) {
        clearTimeout(sequentialRef.current);
      }
    };
  }, []);

  // Focus management when modal opens
  useEffect(() => {
    if (modalOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [modalOpen]);

  /** Handle "Select Roll" checkbox being checked */
  const handleSelectRoll = useCallback((roll: RollGroup) => {
    setSelectedRolls((prev) => new Set([...prev, roll.roll_id]));
    setSelectedScans((prev) => {
      const next = new Set(prev);
      roll.scans.forEach((s) => next.add(s.scan_id));
      return next;
    });
  }, []);

  /** Handle "Select Roll" checkbox being unchecked */
  const handleDeselectRoll = useCallback((roll: RollGroup) => {
    setSelectedRolls((prev) => {
      const next = new Set(prev);
      next.delete(roll.roll_id);
      return next;
    });
    setSelectedScans((prev) => {
      const next = new Set(prev);
      roll.scans.forEach((s) => next.delete(s.scan_id));
      return next;
    });
  }, []);

  /** Toggle roll checkbox */
  const handleToggleRoll = useCallback(
    (roll: RollGroup) => {
      if (selectedRolls.has(roll.roll_id)) {
        handleDeselectRoll(roll);
      } else {
        handleSelectRoll(roll);
      }
    },
    [selectedRolls, handleSelectRoll, handleDeselectRoll]
  );

  /** Toggle individual scan selection with bidirectional roll sync */
  const toggleScanSelection = useCallback(
    (scanId: string, roll: RollGroup) => {
      setSelectedScans((prev) => {
        const next = new Set(prev);
        if (next.has(scanId)) {
          next.delete(scanId);
        } else {
          next.add(scanId);
        }

        // Bidirectional sync: check if all scans in roll are now selected
        const allRollScansSelected =
          roll.scans.length > 0 && roll.scans.every((s) => next.has(s.scan_id));

        if (allRollScansSelected) {
          // Auto-check roll
          setSelectedRolls((prevRolls) => new Set([...prevRolls, roll.roll_id]));
        } else {
          // Auto-uncheck roll
          setSelectedRolls((prevRolls) => {
            const nextRolls = new Set(prevRolls);
            nextRolls.delete(roll.roll_id);
            return nextRolls;
          });
        }

        return next;
      });
    },
    []
  );

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedScans(new Set());
      setSelectedRolls(new Set());
    } else {
      setSelectedScans(new Set(allScans.map((s) => s.scan_id)));
      setSelectedRolls(new Set(rolls.filter((r) => r.scans.length > 0).map((r) => r.roll_id)));
    }
  }, [allSelected, allScans, rolls]);

  const handleDownloadAll = useCallback(() => {
    setDownloadTarget('all');
    setModalOpen(true);
  }, []);

  const handleDownloadSelected = useCallback(() => {
    setDownloadTarget('selected');
    setModalOpen(true);
  }, []);

  /**
   * Auto-download the COMPLETE parts of a multi-part job one after another,
   * spaced by a short delay, updating progress as each fires.
   */
  const startSequentialDownload = useCallback((parts: DownloadPart[]) => {
    const completeParts = parts
      .filter((p) => p.status === 'COMPLETE' && p.presigned_url)
      .sort((a, b) => a.part_no - b.part_no);

    if (completeParts.length === 0) {
      setDownloadProgress(null);
      return;
    }

    if (sequentialRef.current) clearTimeout(sequentialRef.current);

    const total = completeParts.length;
    let index = 0;

    const downloadNext = () => {
      if (index >= total) {
        setDownloadProgress(null);
        sequentialRef.current = null;
        return;
      }
      const part = completeParts[index];
      setDownloadProgress({ current: index + 1, total });
      if (part.presigned_url) {
        triggerPartDownload(part.presigned_url, part.zip_filename);
      }
      index += 1;
      if (index < total) {
        sequentialRef.current = setTimeout(downloadNext, PART_DOWNLOAD_DELAY_MS);
      } else {
        // Keep the final "part N of N" message briefly, then clear it.
        sequentialRef.current = setTimeout(() => {
          setDownloadProgress(null);
          sequentialRef.current = null;
        }, PART_DOWNLOAD_DELAY_MS);
      }
    };

    downloadNext();
  }, []);

  const startPolling = useCallback(
    (id: string) => {
      setPolling(true);
      setDownloadError(null);

      const stop = () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPolling(false);
      };

      pollingRef.current = setInterval(async () => {
        try {
          const result = await pollDownloadStatus(id);
          const multiPart = isMultiPart(result);

          // Keep the parts panel live while a multi-part job progresses.
          if (multiPart) {
            setJobResult(result);
          }

          // Non-terminal states: keep polling.
          if (result.status === 'PENDING' || result.status === 'PROCESSING') {
            return;
          }

          stop();

          if (multiPart) {
            // COMPLETE / PARTIAL / FAILED with multiple parts: render the panel
            // and auto-download the COMPLETE parts in sequence. Retain the job
            // id so the customer can re-download or retry failed parts.
            startSequentialDownload(result.parts!);
            return;
          }

          // --- Single-part path ---
          if (result.status === 'COMPLETE') {
            let ready = result;
            if (isUrlExpired(result.presigned_expires_at)) {
              const refreshed = await refreshDownloadUrl(id);
              ready = {
                ...result,
                presigned_url: refreshed.presigned_url,
                presigned_expires_at: refreshed.presigned_expires_at,
              };
            }

            // Auto-download once (unchanged), but keep the result so a
            // persistent panel offers a re-download. Previously the UI reset to
            // idle here, so a browser that blocked or lost the download left the
            // customer with nothing to click but "rebuild the whole job". The
            // ZIP lives in S3 for ~2 days; re-download just refreshes the URL.
            if (ready.presigned_url) {
              triggerPartDownload(ready.presigned_url);
            }
            setJobResult(ready);
            setJobId(null);
          } else if (result.status === 'FAILED') {
            const scanErrors = result.scan_errors;
            if (scanErrors && scanErrors.length > 0) {
              const errorMessages = scanErrors
                .map((e) => `${e.scan_id}: ${e.error}`)
                .join('; ');
              setDownloadError(`Download generation failed: ${errorMessages}`);
            } else {
              setDownloadError(result.error || 'Download generation failed');
            }
          }
        } catch (err) {
          stop();
          setDownloadError(
            err instanceof Error ? err.message : 'Failed to check download status'
          );
        }
      }, 3000);
    },
    [startSequentialDownload]
  );

  const handleFormatSelect = useCallback(
    async (format: DownloadFormat) => {
      setModalOpen(false);
      setDownloadError(null);
      setJobResult(null);
      setDownloadProgress(null);

      let scanIds: string[];
      let rollIds: string[];

      if (downloadTarget === 'all') {
        // Select all: treat as all rolls selected
        const allRollIds = rolls.filter((r) => r.scans.length > 0).map((r) => r.roll_id);
        const allScanIds = allScans.map((s) => s.scan_id);
        const request = buildDownloadRequest(
          new Set(allRollIds),
          new Set(allScanIds),
          rolls,
          format
        );
        rollIds = request.roll_ids ?? [];
        scanIds = request.scan_ids ?? [];
      } else {
        // Use current selection state
        const request = buildDownloadRequest(selectedRolls, selectedScans, rolls, format);
        rollIds = request.roll_ids ?? [];
        scanIds = request.scan_ids ?? [];
      }

      setLastFormat(format);
      setLastScanIds(scanIds);
      setLastRollIds(rollIds);

      try {
        const { job_id } = await initiateDownload(format, scanIds, rollIds);
        setJobId(job_id);
        startPolling(job_id);
      } catch (err) {
        setDownloadError(
          err instanceof Error ? err.message : 'Failed to start download'
        );
      }
    },
    [downloadTarget, rolls, allScans, selectedRolls, selectedScans, startPolling]
  );

  const handleRetry = useCallback(async () => {
    if (!lastFormat || (lastScanIds.length === 0 && lastRollIds.length === 0)) return;
    setDownloadError(null);
    setJobResult(null);
    setDownloadProgress(null);

    try {
      const { job_id } = await initiateDownload(lastFormat, lastScanIds, lastRollIds);
      setJobId(job_id);
      startPolling(job_id);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : 'Failed to start download'
      );
    }
  }, [lastFormat, lastScanIds, lastRollIds, startPolling]);

  /** (Re-)download a completed single-part job, refreshing an expired URL. */
  const handleManualSingleDownload = useCallback(async () => {
    if (!jobResult) return;
    setDownloadError(null);
    try {
      let url = jobResult.presigned_url;
      if (!url || isUrlExpired(jobResult.presigned_expires_at)) {
        const refreshed = await refreshDownloadUrl(jobResult.job_id);
        url = refreshed.presigned_url;
        setJobResult((prev) =>
          prev
            ? {
                ...prev,
                presigned_url: refreshed.presigned_url,
                presigned_expires_at: refreshed.presigned_expires_at,
              }
            : prev,
        );
      }
      if (url) triggerPartDownload(url);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : 'Failed to refresh download',
      );
    }
  }, [jobResult]);

  /** Manually (re-)download a single COMPLETE part, refreshing an expired URL. */
  const handleManualPartDownload = useCallback(
    async (part: DownloadPart) => {
      if (!jobId) return;
      setDownloadError(null);
      try {
        let url = part.presigned_url;
        if (!url || isUrlExpired(part.presigned_expires_at)) {
          const refreshed = await refreshDownloadUrl(jobId);
          if (isMultiPart(refreshed)) setJobResult(refreshed);
          const fresh = refreshed.parts?.find((p) => p.part_no === part.part_no);
          url = fresh?.presigned_url ?? refreshed.presigned_url;
        }
        if (url) {
          triggerPartDownload(url, part.zip_filename);
        }
      } catch (err) {
        setDownloadError(
          err instanceof Error ? err.message : 'Failed to download part'
        );
      }
    },
    [jobId]
  );

  /** Retry a single FAILED part and resume polling for its completion. */
  const handleRetryPart = useCallback(
    async (part: DownloadPart) => {
      if (!jobId) return;
      setDownloadError(null);
      try {
        await retryDownloadPart(jobId, part.part_no);
        startPolling(jobId);
      } catch (err) {
        setDownloadError(
          err instanceof Error ? err.message : 'Failed to retry part'
        );
      }
    },
    [jobId, startPolling]
  );

  const handleImageError = useCallback((scanId: string) => {
    setFailedImages((prev) => {
      const next = new Set(prev);
      next.add(scanId);
      return next;
    });
  }, []);

  const handleModalKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModalOpen(false);
      }
    },
    []
  );

  const displayOrderName = data?.order_number ?? `Order ${orderId}`;
  const selectedCount = getSelectedCount(selectedScans);

  if (isLoading) {
    return (
      <main className={styles.page}>
        <Link to={`/orders/${orderId}`} className={styles.backLink}>
          ← Back to gallery
        </Link>
        <h1 className={styles.heading}>Download — {displayOrderName}</h1>
        <p className={styles.loading}>Loading scans...</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className={styles.page}>
        <Link to={`/orders/${orderId}`} className={styles.backLink}>
          ← Back to gallery
        </Link>
        <h1 className={styles.heading}>Download — {displayOrderName}</h1>
        <div className={styles.fetchError} role="alert">
          <p>{error instanceof Error ? error.message : 'Something went wrong.'}</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <Link to={`/orders/${orderId}`} className={styles.backLink}>
        ← Back to gallery
      </Link>
      <h1 className={styles.heading}>Download — {displayOrderName}</h1>
      <p className={styles.scanCount}>
        {allScans.length} {allScans.length === 1 ? 'scan' : 'scans'} available
      </p>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <label className={styles.selectAllLabel}>
          <input
            type="checkbox"
            className={styles.selectAllCheckbox}
            checked={allSelected}
            onChange={handleSelectAll}
            disabled={allScans.length === 0}
            aria-label="Select all scans"
          />
          Select All
        </label>
        <button
          type="button"
          className={styles.downloadAllBtn}
          onClick={handleDownloadAll}
          disabled={allScans.length === 0 || polling}
          aria-label="Download all scans"
        >
          Download All
        </button>
        <button
          type="button"
          className={styles.downloadSelectedBtn}
          onClick={handleDownloadSelected}
          disabled={selectedCount === 0 || polling}
          aria-label={`Download ${selectedCount} selected scans`}
        >
          Download Selected ({selectedCount})
        </button>
        {selectedCount > 0 && (
          <span className={styles.selectionHint}>
            {selectedCount} of {allScans.length} selected
          </span>
        )}
      </div>

      {/* Polling progress */}
      {polling && (
        <div className={styles.progress} role="status" aria-live="polite">
          <div className={styles.progressSpinner} aria-hidden="true" />
          <p className={styles.progressText}>Preparing your download...</p>
        </div>
      )}

      {/* Multi-part download panel */}
      {jobResult && jobResult.parts && jobResult.parts.length > 1 && (
        <section className={styles.partsPanel} aria-label="Multi-part download">
          <p className={styles.partsExplanation}>
            This order was large, so your download was split into{' '}
            {jobResult.part_count ?? jobResult.parts.length} ZIPs by roll.
          </p>
          {downloadProgress && (
            <p className={styles.partsProgress} role="status" aria-live="polite">
              Downloading part {downloadProgress.current} of {downloadProgress.total}…
            </p>
          )}
          <ul className={styles.partsList}>
            {jobResult.parts.map((part) => (
              <li key={part.part_no} className={styles.partItem}>
                <span className={styles.partLabel}>{part.label}</span>
                {part.status === 'COMPLETE' && (
                  <button
                    type="button"
                    className={styles.partDownloadBtn}
                    onClick={() => handleManualPartDownload(part)}
                    aria-label={`Download ${part.label}`}
                  >
                    Download
                  </button>
                )}
                {part.status === 'FAILED' && (
                  <>
                    <span className={styles.partFailedText}>Failed</span>
                    <button
                      type="button"
                      className={styles.partRetryBtn}
                      onClick={() => handleRetryPart(part)}
                      aria-label={`Retry ${part.label}`}
                    >
                      Retry
                    </button>
                  </>
                )}
                {(part.status === 'PENDING' || part.status === 'PROCESSING') && (
                  <span className={styles.partPendingText}>Preparing…</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Single-part download panel: keeps a re-download affordance after the
          auto-download, so a blocked or lost download can be retried without
          rebuilding the job. */}
      {jobResult &&
        jobResult.status === 'COMPLETE' &&
        !(jobResult.parts && jobResult.parts.length > 1) && (
          <section className={styles.partsPanel} aria-label="Download ready">
            <p className={styles.partsExplanation}>
              Your download has started. If it didn't begin, or you need it
              again, use the button below.
            </p>
            <ul className={styles.partsList}>
              <li className={styles.partItem}>
                <span className={styles.partLabel}>Your photos</span>
                <button
                  type="button"
                  className={styles.partDownloadBtn}
                  onClick={handleManualSingleDownload}
                  aria-label="Download again"
                >
                  Download again
                </button>
              </li>
            </ul>
          </section>
        )}

      {/* Download error with retry */}
      {downloadError && !polling && (
        <div className={styles.downloadError} role="alert">
          <p className={styles.downloadErrorText}>{downloadError}</p>
          <button
            type="button"
            className={styles.retryBtn}
            onClick={handleRetry}
            aria-label="Retry download"
          >
            Retry
          </button>
        </div>
      )}

      {/* Roll-grouped scan selection */}
      {rollSections.length === 0 ? (
        <p className={styles.emptyState}>No scans available for this order</p>
      ) : (
        rollSections.map((section) => {
          const roll = rolls.find((r) => r.roll_id === section.roll_id)!;
          const rollChecked = selectedRolls.has(section.roll_id);
          const showRollCheckbox = shouldShowRollCheckbox(roll);

          return (
            <section key={section.roll_id} className={styles.rollSection}>
              <div className={styles.rollHeader}>
                <h2 className={styles.rollHeading}>{section.roll_name}</h2>
                {showRollCheckbox && (
                  <label className={styles.rollCheckboxLabel}>
                    <input
                      type="checkbox"
                      className={styles.rollCheckbox}
                      checked={rollChecked}
                      onChange={() => handleToggleRoll(roll)}
                      aria-label={getRollCheckboxLabel(section.roll_name)}
                    />
                    {getRollCheckboxLabel(section.roll_name)}
                  </label>
                )}
              </div>

              {section.scans.length === 0 ? (
                <p className={styles.rollEmptyState}>No scans available in this roll</p>
              ) : (
                <div className={styles.grid}>
                  {section.scans.map((scan) => {
                    const isSelected = selectedScans.has(scan.scan_id);
                    return (
                      <div
                        key={scan.scan_id}
                        className={`${styles.thumbnailCard} ${isSelected ? styles.thumbnailCardSelected : ''}`}
                        onClick={() => toggleScanSelection(scan.scan_id, roll)}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={`Select scan ${scan.original_filename}`}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            toggleScanSelection(scan.scan_id, roll);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={isSelected}
                          readOnly
                          aria-hidden="true"
                          tabIndex={-1}
                        />
                        {failedImages.has(scan.scan_id) ? (
                          <div className={styles.errorPlaceholder}>Failed to load</div>
                        ) : (
                          <img
                            src={scan.thumbnail_url}
                            alt={scan.original_filename}
                            className={styles.thumbnailImage}
                            loading="lazy"
                            onError={() => handleImageError(scan.scan_id)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })
      )}

      {/* Format selection modal */}
      {modalOpen && (
        <div
          className={styles.modalOverlay}
          onClick={() => setModalOpen(false)}
          role="presentation"
        >
          <div
            ref={modalRef}
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="format-modal-heading"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleModalKeyDown}
          >
            <h2 id="format-modal-heading" className={styles.modalHeading}>
              Choose download format
            </h2>
            <div className={styles.formatOptions}>
              {(Object.keys(FORMAT_LABELS) as DownloadFormat[]).map((format) => (
                <button
                  key={format}
                  type="button"
                  className={styles.formatBtn}
                  onClick={() => handleFormatSelect(format)}
                  aria-label={`Download as ${FORMAT_LABELS[format].label}`}
                >
                  <strong>{FORMAT_LABELS[format].label}</strong>
                  <br />
                  <span>
                    {FORMAT_LABELS[format].description}
                    {format === 'archival_tiff' &&
                      hasEdits &&
                      ' — edited images are delivered as JPEG'}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
